import useSWR from "swr";
import { officeSourceId, officeSourcePage } from "./office-source";
import { useAuthContext } from "src/auth/hooks";
import axios, { fetcher } from "src/utils/axios";
import type {
  LicenceOptions,
  LicenceCatalogue,
  LicenceMatrix,
  LicenceCategory,
  LicencePage,
  LicenceApplication,
  IssuedLicence,
} from "src/types/federation-licence";
export const licenceBase = (id: number) => `/providers/${id}/federation`;
export const licenceScope = (workspace?: number) =>
  workspace ? `?workspace_provider_id=${workspace}` : "";
export function useLicenceData<T>(
  federation: number,
  resource: string,
  enabled: boolean,
  params: Record<string, unknown> = {},
) {
  const { user } = useAuthContext();
  const source = resource === "licence-applications" && !params.player_id ? officeSourceId("licences", federation) : 0;
  const selectedResource = source ? `${resource}/${source}` : resource;
  return useSWR<T>(
    enabled && federation > 0 && user?.id
      ? [`${licenceBase(federation)}/${selectedResource}#actor-${user.id}`, { params }]
      : null,
    source ? officeSourcePage : fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
export function useLicenceCapabilities(
  provider: number,
  enabled: boolean,
  federation?: number,
) {
  return useLicenceData<{
    allowed_actions: string[];
    currency: string;
    sports: string[];
    timezone_offset_hours: number;
  }>(
    provider,
    "licences/capabilities",
    enabled,
    federation ? { federation_provider_id: federation } : {},
  );
}
export const useLicenceCatalogue = (id: number, enabled: boolean, offset = 0) =>
  useLicenceData<LicencePage<LicenceCatalogue>>(
    id,
    "licence-catalogue",
    enabled,
    { offset, limit: 100 },
  );
export const useLicenceMatrices = (id: number, enabled: boolean) =>
  useLicenceData<{ items: LicenceMatrix[] }>(id, "licence-mappings", enabled);
export const useLicenceCategories = (id: number, enabled: boolean) =>
  useLicenceData<{ items: LicenceCategory[] }>(
    id,
    "licence-mappings/categories",
    enabled,
  );
export const useLicenceOptions = (
  id: number,
  player: number,
  enabled: boolean,
  workspace?: number,
) =>
  useLicenceData<LicenceOptions>(id, `licence-options/${player}`, enabled, {
    workspace_provider_id: workspace,
  });
export const useLicenceApplications = (
  id: number,
  enabled: boolean,
  workspace?: number,
  player?: number,
  offset = 0,
) =>
  useLicenceData<LicencePage<LicenceApplication>>(
    id,
    "licence-applications",
    enabled,
    { workspace_provider_id: workspace, player_id: player, offset, limit: 20 },
  );
export const useIssuedLicences = (
  id: number,
  enabled: boolean,
  workspace?: number,
  player?: number,
  offset = 0,
  at = "",
) =>
  useLicenceData<LicencePage<IssuedLicence>>(id, "licences", enabled, {
    workspace_provider_id: workspace,
    player_id: player,
    offset,
    limit: 20,
    ...(at && { at }),
  });
export async function licenceCommand<T = unknown>(
  url: string,
  payload: Record<string, unknown>,
) {
  return (await axios.post<T>(url, payload)).data;
}
export function licenceError(error: unknown) {
  // The shared Axios interceptor rejects with the response body, so callers
  // usually receive { detail } without the original HTTP status.
  const failure = error as {
    detail?: unknown;
    response?: { status?: number; data?: { detail?: unknown } };
  } | null;
  const status = failure?.response?.status;
  const detail = failure?.response?.data?.detail ?? failure?.detail;
  if (detail === "federation_debt_action_blocked") return "debt_action_blocked";
  if (
    detail === "federation_licence_setup_required" ||
    detail === "federation_decision_capability_unavailable" ||
    detail === "federation_decision_unavailable" ||
    (typeof detail === "string" &&
      detail.startsWith("Invalid provider timezone:"))
  )
    return "fl_setup";
  if (
    typeof detail === "string" &&
    [
      "federation_licence_permission_denied",
      "federation_licence_not_found",
      "federation_licence_unavailable",
      "federation_licence_payer_unavailable",
      "federation_decision_permission_denied",
      "federation_decision_target_not_found",
      "federation_player_permission_denied",
      "federation_player_verified_account_required",
    ].includes(detail)
  )
    return "fl_denied";
  if (Array.isArray(detail)) return "fl_invalid";
  if (typeof detail === "string" && detail.startsWith("federation_licence_"))
    return licenceReason(detail);
  return status === 401 || status === 403 || status === 404
    ? "fl_denied"
    : status === 409
    ? "fl_conflict"
    : status === 503
    ? "fl_setup"
    : status === 422
    ? "fl_invalid"
    : "fl_error";
}

export function licenceReason(code: string | null | undefined) {
  if (code === 'federation_debt_action_blocked') return 'debt_action_blocked';
  const value = (code || "").replace(/^federation_(licence|finance)_/, "");
  if (value === "payment_required") return "fl_payment_required";
  if (
    [
      "permission_denied",
      "self_approval",
      "payer_unavailable",
      "not_found",
    ].includes(value)
  )
    return "fl_denied";
  if (
    [
      "version_conflict",
      "source_conflict",
      "revision_conflict",
      "quote_conflict",
    ].includes(value)
  )
    return "fl_conflict";
  return [
    "purchase_window",
    "age_condition",
    "affiliation_required",
    "already_issued",
    "application_pending",
    "coexistence_unavailable",
    "upgrade_path_unavailable",
    "negative_upgrade_difference",
    "upgrade_period_unavailable",
  ].includes(value)
    ? `fl_reason_${value}`
    : "fl_invalid";
}
export const useLicenceMemberships = (workspace: number, enabled: boolean) =>
  useLicenceData<{ items: { id: number; label: string }[] }>(
    workspace,
    "licence-memberships",
    enabled,
  );
export function useOwnLicencePlayers() {
  const { user } = useAuthContext();
  return useSWR<{
    items: {
      player_id: number;
      federation_provider_id: number;
      federation_label: string;
      number: number;
    }[];
  }>(
    user?.id
      ? `/providers/federation-licences/my-players#actor-${user.id}`
      : null,
    fetcher,
    { keepPreviousData: false, shouldRetryOnError: false },
  );
}
